/**
 * Tour Search Module
 * Handles the search functionality for tours, including:
 * - Modal-based filters for destinations, passengers, and departure points
 * - Transport type tabs (coach, air, cruise, etc.)
 * - Search results pagination
 * - Dynamic filter updates
 */

import { createSelectors, createPrefixSelector } from '../utils/selectorUtils';
import { ajax } from '../utils/ajaxUtils';
import { ModalDestination } from './modalDestination';
import { createLogger } from '../utils/debugLogger';
import { ModalDeparturePoint } from './modalDeparturePoint';
import { ModalPassengers } from './modalPassengers';
import { ModalHolidayType } from './modalHolidayType';
import { ModalDepartureDate } from './modalDepartureDate';
import { ModalDuration } from './modalDuration';
import { ModalPrice } from './modalPrice';
import { modalJS, disposeModalJS } from '../modalJS';
import { initializeTooltips } from '../tooltipJS';


const l = createLogger('tourSearch.js');

// DOM selectors for various UI elements
const selectors = createSelectors({
    DISPLAY_MODE: 'data-ts-search-display-mode',           // Controls search results update mode (filters or full results)
    SEARCH_BUTTON: 'data-ts-search',
    TAB_DEFAULT_SEARCH_TYPE_VAL: 'data-ts-search-tab-default',
    TAB_SEARCH_TYPES: 'data-ts-search-tab',               // Transport type tab selectors
    TAB_SHOW_COACH: 'data-ts-search-tab-coach-show',      // Visibility controls for transport tabs
    TAB_SHOW_HOTEL: 'data-ts-search-tab-hotel-show',
    TAB_SHOW_AIR: 'data-ts-search-tab-air-show',
    TAB_SHOW_CRUISE: 'data-ts-search-tab-cruise-show',
    TAB_SHOW_RAIL: 'data-ts-search-tab-rail-show',
    TAB_SHOW_SPECIALIST: 'data-ts-search-tab-specialist-show',
    SEARCH_RESULTS_CONTAINER: 'data-ts-update-target',
    // SEARCH_FILTERS_CONTAINER: 'data-ts-filters',
    CURRENT_DESTINATION: 'data-ts-search-currentdestination', //DestinationName for current DestinationPage
    CURRENT_HOLIDAY_TYPE: 'data-ts-search-currentholidaytype',
    CURRENT_EXPERT: 'data-ts-search-currentexpert',
    PAGINATION_TRIGGER: 'data-ts-pagination-offset',
    SORT_BY: 'data-ts-sort-by',
    CLEAR_ALL_BUTTON: 'data-ts-clear-all',
    NEAREST_DEPARTUREPOINT_SELECT: `data-ts-departure-nearest-select`,
    FORCED_DEPARTURE_START_DATE: 'data-ts-search-forced-date-start',
    FORCED_DEPARTURE_DATE_PLUS: 'data-ts-search-forced-date-plus',
    FORCED_DEPARTURE_DATE_MINUS: 'data-ts-search-forced-date-minus'
});

// API endpoints for various search operations
const settings = Object.freeze({
    SEARCH_RESULTS_PATH: '/toursearch/toursearchresults',
    SEARCH_UPDATE_FILTERS: '/toursearch/toursearchfilter',
    SEARCH_UPDATE_ALL: '/toursearch/update',
    SEARCH_UPDATE_PAGINATION: '/toursearch/updatepagination'
});

// Module state variables
const modals = [];                              // Active modal instances
let defaultSearchType;                          // Default transport type
let selectedSearchType;                         // Currently selected transport type
let tabShowCoach, tabShowCruise, tabShowHotel,
    tabShowRail, tabShowAir, tabShowSpecialist; // Tab visibility states
let currentHolidayType, currentDestination,
    currentExpert;                              // Current page context
let displayMode;                                // Search results display mode. Controls the filters shown and tour card styles
let updateSearchPath;                           // Current API endpoint
let updateContainer;                            // Current update target container
let sortByOption;                               // Current sort by option
let forcedDepartureStartDate,                   // Forced departure dates. Set by the server depending on page, not user.
    forcedDeparturePlus,
    forcedDepartureMinus;
let currentQueryString;                         //Used to detect when search filter changes have been made

// Event handler references for cleanup
let searchTabHandler;
let searchButtonHandler;
let clearButtonHandler;
let modalClosedHandler;
let sortByHandler;
let nearestDeparturePointSelectHandler;
let popStateHandler;

const tourSearch = (() => {

    /**
 * Query String Building Functions
 */

    // Create a query string manager object that will hold our state functions
    const queryStringManager = {
        buildStateQueryString: (pageOffset = 0) => {
            const queryString = new URLSearchParams();

            queryString.append("toursearchtype", selectedSearchType);
            queryString.append("tco", tabShowCoach);
            queryString.append("tcr", tabShowCruise);
            queryString.append("tho", tabShowHotel);
            queryString.append("tai", tabShowAir);
            queryString.append("tra", tabShowRail);
            queryString.append("tex", tabShowSpecialist);
            queryString.append("ch", currentHolidayType);
            queryString.append("cd", currentDestination);
            queryString.append("ce", currentExpert);
            queryString.append("dm", displayMode);
            queryString.append("sortinfo", sortByOption);

            //forced departure dates - might not be set
            queryString.append("fds", forcedDepartureStartDate);
            queryString.append("fdp", forcedDeparturePlus);
            queryString.append("fdm", forcedDepartureMinus);

            if (pageOffset > 0) {
                queryString.append("po", pageOffset);
            }

            //Add Num Passengers
            modals
                .filter(modal => modal.modalName === "ModalPassengers")
                .forEach(modal => {
                    const arr = modal.getSelectedValues();
                    for (const [key, value] of arr) {
                        queryString.append(key, value);
                    }
                });



            cleanQueryString(queryString);

            return queryString;
        },

        buildUserQueryString(modalNameToIgnore = null) {
            const queryString = new URLSearchParams();
            modals
                .filter(modal => modal.modalName !== "ModalPassengers")
                .forEach(modal => {
                    const arr = modal.getSelectedValues();
                    for (const [key, value] of arr) {
                        queryString.append(key, value);
                    }
                });
            cleanQueryString(queryString);
            return queryString;
        }
    };


    /**
     * Core Initialization and Update Functions
     */

    const initialize = ({ skipSettingHistoryState = false } = {}) => {
        l.log("tourSearch: initialize");
        let enabled = getInitialState();
        if (!enabled) return;

        modalJS();

        // Create and store modal instances
        modals.push(ModalDestination.createModal(queryStringManager));
        modals.push(ModalPassengers.createModal(queryStringManager));
        modals.push(ModalDeparturePoint.createModal(queryStringManager));
        modals.push(ModalHolidayType.createModal(queryStringManager));
        modals.push(ModalDepartureDate.createModal(queryStringManager));
        modals.push(ModalDuration.createModal(queryStringManager));
        modals.push(ModalPrice.createModal(queryStringManager));

        // Attach all event handlers
        attachModalEvents();
        attachSearchButtonEvents();
        attachSearchTabEvents();
        attachPagination();
        attachHistoryEvents(); 

        currentQueryString = new URLSearchParams([...queryStringManager.buildStateQueryString(), ...queryStringManager.buildUserQueryString()]);

        // Sets the push state for the initial page load
        if (!skipSettingHistoryState && (displayMode === 'SearchResults' || displayMode === 'LastMinuteHolidays')) {
            const initialQs = new URLSearchParams([...queryStringManager.buildStateQueryString(), ...queryStringManager.buildUserQueryString()]);
            l.log("inital push state", initialQs);
            history.replaceState(
                { previousQueryString: initialQs.toString() },
                '',
                `${window.location.pathname}`//?${initialQs.toString()}
            );
        }
    };

    const getInitialState = () => {
        displayMode = document.querySelector(selectors.DISPLAY_MODE)?.value;

        if (displayMode == null) {
            l.log("No Tour Search Elements Found. Disabling.");
            return false;
        }

        // Load tab visibility states from DOM
        tabShowCoach = document.querySelector(selectors.TAB_SHOW_COACH).value;
        tabShowAir = document.querySelector(selectors.TAB_SHOW_AIR).value;
        tabShowCruise = document.querySelector(selectors.TAB_SHOW_CRUISE).value;
        tabShowHotel = document.querySelector(selectors.TAB_SHOW_HOTEL).value;
        tabShowRail = document.querySelector(selectors.TAB_SHOW_RAIL).value;
        tabShowSpecialist = document.querySelector(selectors.TAB_SHOW_SPECIALIST).value;

        // Load page context
        currentDestination = document.querySelector(selectors.CURRENT_DESTINATION).value;
        currentHolidayType = document.querySelector(selectors.CURRENT_HOLIDAY_TYPE).value;
        currentExpert = document.querySelector(selectors.CURRENT_EXPERT).value;
      

        // Set initial search type
        defaultSearchType = document.querySelector(selectors.TAB_DEFAULT_SEARCH_TYPE_VAL).value;
        selectedSearchType = defaultSearchType;

        // Configure update behavior based on display mode
        updateSearchPath = settings.SEARCH_UPDATE_FILTERS;
        updateContainer = selectors.SEARCH_RESULTS_CONTAINER;

        //Optional. Only used on destination pages with a forced date range or LMH
        forcedDepartureStartDate = document.querySelector(selectors.FORCED_DEPARTURE_START_DATE)?.value;
        forcedDeparturePlus = document.querySelector(selectors.FORCED_DEPARTURE_DATE_PLUS)?.value;
        forcedDepartureMinus = document.querySelector(selectors.FORCED_DEPARTURE_DATE_MINUS)?.value;

        if (displayMode === 'SearchResults' || displayMode === 'LastMinuteHolidays') {
            sortByOption = document.querySelector(selectors.SORT_BY).value;
            updateSearchPath = settings.SEARCH_UPDATE_ALL;
        }

        return true;
    };

    /**
     * Search Results Update Functions
     */

    const updateSearchResults = async ({ useUserFilters = true, popStateQueryString = "" } = {}) => {
        l.log(`tourSearch: updateSearchResults for ${displayMode}`);

        let qs;
        if (popStateQueryString) {
            qs = popStateQueryString;
        } else {
            // Build query string with optional user filters
            qs = queryStringManager.buildStateQueryString();
            if (useUserFilters) {
                qs = new URLSearchParams([...qs, ...queryStringManager.buildUserQueryString()]);
            }
        }

        let filtersHaveChanged = false;
        if (currentQueryString.toString() != qs.toString()) {
            filtersHaveChanged = true;
        }

        // Clean up existing events and content
        dispose();

        // Fetch and update content
        try {
            const response = await ajax(`${updateSearchPath}?${qs.toString()}`);

            const searchResultsContainer = document.querySelector(updateContainer);
            searchResultsContainer.innerHTML = response;

            if (!popStateQueryString && (displayMode === "SearchResults" || displayMode === "LastMinuteHolidays")) {
                history.pushState(
                    { previousQueryString: qs.toString() },
                    '',
                    `${window.location.pathname}?${qs.toString()}`
                );
            }

            // Notify of content update and reinitialize
            document.dispatchEvent(new CustomEvent('dynamicContentLoaded', { bubbles: true }));
            initialize({ skipSettingHistoryState: true });

            //Scroll to top when results have updated
            if (filtersHaveChanged && window.location.pathname !== '/') { 
                searchResultsContainer.scrollIntoView();
            }            

        } catch (error) {
            l.error("Failed to update search results:", error);
            throw error;
        }
    };

    // Back button clicked. Attempt to restore previous search
    const attachHistoryEvents = async () => {
        popStateHandler = async (event) => {
            l.log("popStateHandler", event);
            if (event.state && event.state.previousQueryString) {
                let previousQueryString = event.state.previousQueryString;
                l.log("popStateHandler", previousQueryString);
                await updateSearchResults({ useUserFilters: false, popStateQueryString: previousQueryString });
            }
        }

        window.addEventListener('popstate', popStateHandler);
    }

    /**
     * Pagination Functions
     */

    const attachPagination = async () => {
        const element = document.querySelector(selectors.PAGINATION_TRIGGER);
        if (!element) {
            l.log("tourSearch: attachPagination - No Pagination Element Found");
            return;
        }

        // Create intersection observer for infinite scroll
        const observer = new IntersectionObserver(async (entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                observer.disconnect();

                // Small delay to trigger pagination, allow go past pagination point before inserting new content
                setTimeout(async () => {
                    const pageOffset = parseInt(entry.target.dataset.tsPaginationOffset);
                    await updatePagination({ pageOffset });
                    await attachPagination();
                }, 2000);
            }
        }, {
            threshold: 0.1,      // Trigger when 10% of element is visible
            rootMargin: '50px'   // Trigger 50px before element enters viewport
        });

        observer.observe(element);
    };

    const updatePagination = async ({ pageOffset = 0 } = {}) => {
        l.log(`tourSearch: updatePagination for ${pageOffset}`);

        // Build query string with pagination offset
        let qs = new URLSearchParams([...queryStringManager.buildStateQueryString(pageOffset), ...queryStringManager.buildUserQueryString()]);

        // Fetch and insert new content
        try {
            const response = await ajax(`${settings.SEARCH_UPDATE_PAGINATION}?${qs.toString()}`);
            const searchResultsContainer = document.querySelector(selectors.PAGINATION_TRIGGER);
            searchResultsContainer.outerHTML = response;

            document.dispatchEvent(new CustomEvent('dynamicContentLoaded', { bubbles: true }));
        } catch (error) {
            l.error("Failed to update search results:", error);
            throw error;
        }
    };

    /**
     * Event Attachment Functions
     */

    const attachModalEvents = () => {
        l.log("tourSearch: attachModalTriggers");
        // Attach trigger events to all modals
        modals.forEach(modal => {
            modal.attachModalTriggerEvent();
        });

        // Handle modal close events
        modalClosedHandler = async (e) => {
            const modalName = e.detail.modalName;
            l.log("tourSearch: modalClosed", modalName);
            await updateSearchResults();
        };

        document.addEventListener("modalClosed", modalClosedHandler);
    };

    const attachSearchTabEvents = () => {
        // Attach tab events
        searchTabHandler = async (tab) => {
            const newTab = tab.dataset.tsSearchTab;
            if (selectedSearchType !== newTab) {
                selectedSearchType = newTab;
                await updateSearchResults({ useUserFilters: false });
            }
        };

        const searchTabs = document.querySelectorAll(selectors.TAB_SEARCH_TYPES);
        searchTabs.forEach(tab => {
            tab.addEventListener('click', () => searchTabHandler(tab));
        });


        // Attach sort by event
        if (document.querySelector(selectors.SORT_BY)) {
            sortByHandler = async (e) => {
                sortByOption = e.target.value;
                l.log("tourSearch: Sort changed to", sortByOption);
                await updateSearchResults();
            }
            const sortBySelector = document.querySelector(selectors.SORT_BY);
            sortBySelector.addEventListener('change', (e) => sortByHandler(e))
        };

        // Outside of Tour Search Filters.
        // Appears in the Search Results when Selected Departure Point returns no tours
        if (document.querySelector(selectors.NEAREST_DEPARTUREPOINT_SELECT)) {
            nearestDeparturePointSelectHandler = async (e) => {
                const selectedDeparturePoint = e.target.value;
                l.log("tourSearch: nearestDeparturePointSelectHandler changed to", selectedDeparturePoint);
                if (selectedDeparturePoint === '') {
                    return;
                }

                const modal = modals.find(modal => modal.modalName === 'ModalDeparturePoint');
                if (modal) {
                    modal.setSelectedDeparturePoint(selectedDeparturePoint);
                } else {
                    l.error('ModalDeparturePoint not found');
                }
                await updateSearchResults();
            }
            const nearestDeparturePointSelectSelector = document.querySelector(selectors.NEAREST_DEPARTUREPOINT_SELECT);
            nearestDeparturePointSelectSelector.addEventListener('change', (e) => nearestDeparturePointSelectHandler(e))


        }
    };

    const attachSearchButtonEvents = () => {
        // Attach search button event - Non Search Results
        if (displayMode !== 'SearchResults') {
            searchButtonHandler = (e) => {
                e.preventDefault();
                const qs = new URLSearchParams([...queryStringManager.buildStateQueryString(), ...queryStringManager.buildUserQueryString()]);
                //force the DisplayMode to SearchResults as it'll be set in the push state on the next page
                qs.set("mode", "SearchResults"); 

                const searchResultsUrl = `${settings.SEARCH_RESULTS_PATH}?${qs}`;

                l.log("tourSearch: Search Button Clicked", searchResultsUrl);
                window.location = searchResultsUrl;
            };

            document.querySelector(selectors.SEARCH_BUTTON)
                .addEventListener('click', searchButtonHandler);
        }

        // Attach clear button event - Search Results 
        if (displayMode === 'SearchResults' || displayMode === 'LastMinuteHolidays') {
            clearButtonHandler = async (e) => {
                e.preventDefault();
                l.log("tourSearch: Clear Button Clicked");
                await updateSearchResults({ useUserFilters: false });
            };

            document.querySelector(selectors.CLEAR_ALL_BUTTON)
                .addEventListener('click', clearButtonHandler);
        }
    };





    const cleanQueryString = (qs) => {
        // Create an array of keys to delete
        const keysToDelete = [];

        // First identify all keys to delete
        for (const [key, value] of qs.entries()) {
            if (!value || value === 'null' || value === 'undefined') {
                keysToDelete.push(key);
            }
        }

        // Then delete them
        keysToDelete.forEach(key => qs.delete(key));

        return qs;
    };

    /**
     * Cleanup Function
     */

    const dispose = () => {
        l.log("tourSearch: dispose");

        // Clean up modals
        modals.forEach(modal => modal.dispose());
        modals.length = 0;

        // Remove tab events
        if (searchTabHandler) {
            document.querySelectorAll(selectors.TAB_SEARCH_TYPES)
                .forEach(tab => tab.removeEventListener('click', searchTabHandler));
        }

        // Remove sort by handler
        if (sortByHandler) {
            document.querySelector(selectors.SORT_BY).removeEventListener('change', sortByHandler);
        }

        // Remove button event
        if (searchButtonHandler) {
            const searchButton = document.querySelector(selectors.SEARCH_BUTTON);
            if (searchButton) {
                searchButton.removeEventListener('click', searchButtonHandler);
            }
        }

        // Remove modal event
        if (modalClosedHandler) {
            document.removeEventListener("modalClosed", modalClosedHandler);
        }

        if (popStateHandler) {
            window.removeEventListener("popstate", popStateHandler);
        }

        // Clean up other modules
        disposeModalJS();
        initializeTooltips.cleanup(document.querySelector(updateContainer));

        // Reset all state
        [defaultSearchType, selectedSearchType, tabShowCoach, tabShowCruise,
            tabShowHotel, tabShowRail, tabShowAir, tabShowSpecialist,
            currentHolidayType, currentDestination, currentExpert,
            displayMode].forEach(variable => variable = undefined);
    };

    // Initialize on creation
    initialize();
})();

export { tourSearch };